
import React, { useState, useEffect } from 'react';
import { UsageSummary, DashboardOverviewResponse } from '../types';
import Card from '../components/ui/Card';
import { api } from '../services/api';
import UsageComposedChart from '../components/ui/UsageComposedChart';
import { CartValueKPI } from '../components/ui/CartValueKPI';
import { SalesPerformanceKPI } from '../components/ui/SalesPerformanceKPI';
import { MessageSquare, Users, Clock, ArrowRight, Server, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSubscriptionError } from '../context/SubscriptionErrorContext';
import { useAuth } from '../hooks/useAuth';
import { handleApiError } from '../utils/errorHandling';
import { getDisplayPercentage } from '../utils/percentageUtils';
import { ViewDetailsButton } from '../components/ui/ViewDetailsButton';
import { useInsightFormula } from '../hooks/useInsightFormula';
import Modal from '../components/ui/Modal';

const DashboardPage: React.FC = () => {
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardOverviewResponse | null>(null);
  const [previousMonthData, setPreviousMonthData] = useState<DashboardOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<{ title: string; type: string } | null>(null);
  const [dateOffset, setDateOffset] = useState(0);
  const [cartValueDateOffset, setCartValueDateOffset] = useState(0);
  const [salesPerfDateOffset, setSalesPerfDateOffset] = useState(0);
  const { setSubscriptionError } = useSubscriptionError();
  const { user } = useAuth();

  const calculateDelta = (current: number, previous: number): number => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const usageData = await api.get<UsageSummary>('/api/subscriptions/tenant/usage');
        setUsage(usageData);

        const formatDate = (date: Date) => {
          return date.toISOString().split('T')[0];
        };

        // Current period: let backend use subscription cycle by not sending dates
        // Previous period: same day last month to 30 days before that
        const previousEndDate = new Date();
        previousEndDate.setMonth(previousEndDate.getMonth() - 1);
        const previousStartDate = new Date(previousEndDate);
        previousStartDate.setDate(previousStartDate.getDate() - 30);

        const [currentData, previousData] = await Promise.all([
          api.getDashboardOverview(), // Default to current cycle
          api.getDashboardOverview(
            formatDate(previousStartDate),
            formatDate(previousEndDate)
          )
        ]);

        setDashboardData(currentData);
        setPreviousMonthData(previousData);

        setError(null);
        setSubscriptionError(null);
      } catch (err: any) {
        handleApiError(err, setError, setSubscriptionError);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
        <div className="flex justify-center items-center h-full">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
    );
  }

  if (error) {
      return <div className="text-center text-red-500">{error}</div>
  }
  
  if (!usage || !dashboardData) {
      return <div className="text-center text-text-secondary">No data available.</div>
  }

  const billingPeriodStart = new Date(usage.subscription.billing.currentPeriodStart);
  const billingPeriodEnd = new Date(usage.subscription.billing.currentPeriodEnd);

  // Helper to get date window for filtering (relative to today)
  const getDateWindow = (offset: number) => {
    const end = new Date();
    end.setDate(end.getDate() + offset);
    
    // Ensure end doesn't go before billing period start
    const effectiveEnd = end < billingPeriodStart ? new Date(billingPeriodStart) : end;
    
    const start = new Date(effectiveEnd);
    start.setDate(start.getDate() - 6);
    
    // Ensure start doesn't go before billing period start
    const effectiveStart = start < billingPeriodStart ? new Date(billingPeriodStart) : start;
    
    return {
      start: effectiveStart.toISOString().split('T')[0],
      end: effectiveEnd.toISOString().split('T')[0]
    };
  };

  const usageWindow = getDateWindow(dateOffset);
  const cartValueWindow = getDateWindow(cartValueDateOffset);
  const salesPerfWindow = getDateWindow(salesPerfDateOffset);

  // Filter series to only include dates within current billing period
  const costSeriesInPeriod = (dashboardData?.data?.usageOverview?.dailyCostBreakdown || []).filter(
    entry => entry.date >= billingPeriodStart.toISOString().split('T')[0]
  );

  const usageChartData = costSeriesInPeriod
    .filter(day => day.date >= usageWindow.start && day.date <= usageWindow.end)
    .map(day => ({
      time: day.date,
      usage: day.tokens,
      dailyCost: day.cost,
      cumulativeCost: day.cumulativeCost
    }));
  
  // Extract current and previous period costs for trend calculation
  const currentCostTotal = dashboardData?.data?.usageOverview?.totals?.cost || 0;
  const previousCostTotal = previousMonthData?.data?.usageOverview?.totals?.cost || 0;
  
  const costMetrics = {
    cumulative: currentCostTotal,
    avgPerDay: dashboardData?.data?.usageOverview?.averages?.costPerDay || 0,
    projected: dashboardData?.data?.usageOverview?.projectedUsageCost || dashboardData?.data?.usageOverview?.projectedMonthlyCost || 0,
    trendPct: calculateDelta(currentCostTotal, previousCostTotal)
  };
  
  const currentProductMentions = dashboardData?.data?.productInterest?.sessionsWithProductMentions || 0;
  const previousProductMentions = previousMonthData?.data?.productInterest?.sessionsWithProductMentions || 0;
  
  // Transform cost series for AreaChart
  const cartValueSeries = costSeriesInPeriod
    .filter(entry => entry.date >= cartValueWindow.start && entry.date <= cartValueWindow.end)
    .map(entry => ({
      name: entry.date,
      value: entry.cumulativeCost
    }));
  
  const cartValueData = {
    total: currentProductMentions,
    deltaPct: calculateDelta(currentProductMentions, previousProductMentions),
    series: cartValueSeries
  };

  const currentConversionRate = dashboardData?.data?.salesPerformance?.intentConversionRate || 0;
  const previousConversionRate = previousMonthData?.data?.salesPerformance?.intentConversionRate || 0;
  
  const currentCartIntents = dashboardData?.data?.salesPerformance?.cartIntentsGenerated || 0;
  const previousCartIntents = previousMonthData?.data?.salesPerformance?.cartIntentsGenerated || 0;
  
  const currentAvgCartValue = dashboardData?.data?.salesPerformance?.avgCartValue || 0;
  const previousAvgCartValue = previousMonthData?.data?.salesPerformance?.avgCartValue || 0;
  
  const currentConversationsWithIntent = dashboardData?.data?.salesPerformance?.conversationsWithIntent || 0;
  const previousConversationsWithIntent = previousMonthData?.data?.salesPerformance?.conversationsWithIntent || 0;
  
  const currentRevenue = dashboardData?.data?.salesPerformance?.revenue || 0;
  const previousRevenue = previousMonthData?.data?.salesPerformance?.revenue || 0;
  
  // Transform sales performance series for ComposedChart
  const salesPerfSeries = (dashboardData?.data?.salesPerformance?.series || [])
    .filter(entry => entry.date >= salesPerfWindow.start && entry.date <= salesPerfWindow.end)
    .map(entry => ({
      date: entry.date,
      conversionRate: entry.conversionRate,
      intents: entry.cartIntents
    }));
  
  const salesPerfData = {
    conversionRate: currentConversionRate,
    deltaPct: calculateDelta(currentConversionRate, previousConversionRate),
    orders: currentCartIntents,
    ordersDelta: calculateDelta(currentCartIntents, previousCartIntents),
    carts: currentConversationsWithIntent,
    cartsDelta: calculateDelta(currentConversationsWithIntent, previousConversationsWithIntent),
    avgOrderValue: currentAvgCartValue,
    avgOrderValueDelta: calculateDelta(currentAvgCartValue, previousAvgCartValue),
    revenue: currentRevenue,
    revenueDelta: calculateDelta(currentRevenue, previousRevenue),
    series: salesPerfSeries
  };

  const currentTotalConversations = dashboardData?.data?.usageOverview?.conversationSummary?.total || 0;
  const previousTotalConversations = previousMonthData?.data?.usageOverview?.conversationSummary?.total || 0;
  const conversationsDelta = calculateDelta(currentTotalConversations, previousTotalConversations);

  const currentSDKConversations = dashboardData?.data?.usageOverview?.conversationSummary?.sdk || 0;
  const previousSDKConversations = previousMonthData?.data?.usageOverview?.conversationSummary?.sdk || 0;
  const sdkConversationsDelta = calculateDelta(currentSDKConversations, previousSDKConversations);

  const currentMerchantConversations = dashboardData?.data?.usageOverview?.conversationSummary?.merchant || 0;
  const previousMerchantConversations = previousMonthData?.data?.usageOverview?.conversationSummary?.merchant || 0;
  const merchantConversationsDelta = calculateDelta(currentMerchantConversations, previousMerchantConversations);

  const currentSDKActiveUsers = dashboardData?.data?.usageOverview?.activeUsersByChannel?.sdk || 0;
  const previousSDKActiveUsers = previousMonthData?.data?.usageOverview?.activeUsersByChannel?.sdk || 0;
  const sdkActiveUsersDelta = calculateDelta(currentSDKActiveUsers, previousSDKActiveUsers);
  
  const getDateRange = () => {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + dateOffset);
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 6);
    
    const formatDate = (date: Date) => {
      return `${date.getDate()}/${date.getMonth() + 1}`;
    };
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const getCartValueDateRange = () => {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + cartValueDateOffset);
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 6);
    
    const formatDate = (date: Date) => {
      return `${date.getDate()}/${date.getMonth() + 1}`;
    };
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const handlePreviousWeek = () => {
    const newOffset = dateOffset - 7;
    const newEndDate = new Date();
    newEndDate.setDate(newEndDate.getDate() + newOffset);
    
    // Only allow going back if the end of the new window is after or at the billing period start
    if (newEndDate >= billingPeriodStart) {
      setDateOffset(newOffset);
    }
  };

  const handleNextWeek = () => {
    if (dateOffset < 0) {
      setDateOffset(Math.min(dateOffset + 7, 0));
    }
  };

  const isAtPresent = dateOffset === 0;
  
  const checkIsAtStart = (offset: number) => {
    const d = new Date();
    d.setDate(d.getDate() + offset - 7);
    return d < billingPeriodStart;
  };

  const isAtBillingCycleStart = checkIsAtStart(dateOffset);

  const handleCartValuePreviousWeek = () => {
    const newOffset = cartValueDateOffset - 7;
    const newEndDate = new Date();
    newEndDate.setDate(newEndDate.getDate() + newOffset);
    if (newEndDate >= billingPeriodStart) {
      setCartValueDateOffset(newOffset);
    }
  };

  const handleCartValueNextWeek = () => {
    if (cartValueDateOffset < 0) {
      setCartValueDateOffset(Math.min(cartValueDateOffset + 7, 0));
    }
  };

  const isCartValueAtPresent = cartValueDateOffset === 0;
  const isCartValueAtBillingCycleStart = checkIsAtStart(cartValueDateOffset);

  const handleSalesPerfPreviousWeek = () => {
    const newOffset = salesPerfDateOffset - 7;
    const newEndDate = new Date();
    newEndDate.setDate(newEndDate.getDate() + newOffset);
    if (newEndDate >= billingPeriodStart) {
      setSalesPerfDateOffset(newOffset);
    }
  };

  const handleSalesPerfNextWeek = () => {
    if (salesPerfDateOffset < 0) {
      setSalesPerfDateOffset(Math.min(salesPerfDateOffset + 7, 0));
    }
  };

  const isSalesPerfAtPresent = salesPerfDateOffset === 0;
  const isSalesPerfAtBillingCycleStart = checkIsAtStart(salesPerfDateOffset);

  const getSalesPerfDateRange = () => {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + salesPerfDateOffset);
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 6);
    
    const formatDate = (date: Date) => {
      return `${date.getDate()}/${date.getMonth() + 1}`;
    };
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const { getAIInsight } = useInsightFormula(usage);

  const handleViewDetails = (type: 'cumulative' | 'cartValue' | 'salesPerformance') => {
    const titles = {
      cumulative: 'Usage Details',
      cartValue: 'Product Interest Analytics',
      salesPerformance: 'Sales Performance Details',
    };
    setModalContent({ title: titles[type], type });
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-8 pb-8">
      <div>
        <h1 className="text-3xl font-bold dark:text-text-primary text-light-text-primary">Dashboard</h1>
        <p className="dark:text-text-secondary text-light-text-secondary">
          Welcome back{user?.personalInfo?.firstName ? ` ${user.personalInfo.firstName}` : ''}! Here's a summary of your account.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Usage Card */}
        <Card title="Usage Overview via Mentoros" className="lg:col-span-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 p-4">
            {/* Usage Chart with Button */}
            <div className="flex flex-col">
              <UsageComposedChart 
                data={usageChartData}
                title="Usage"
                dateRange={getDateRange()}
                onPrevious={handlePreviousWeek}
                onNext={handleNextWeek}
                canGoPrevious={!isAtBillingCycleStart}
                canGoNext={!isAtPresent}
                onReturnToPresent={() => setDateOffset(0)}
                isAtPresent={isAtPresent}
              />
              <div className="flex justify-center mt-4">
                <ViewDetailsButton label="View Details" onClick={() => handleViewDetails('cumulative')} />
              </div>
            </div>

            {/* Product Interest Chart with Button */}
            <div className="flex flex-col">
              <CartValueKPI 
                title="Product Interest"
                total={cartValueData.total}
                deltaPct={cartValueData.deltaPct}
                series={cartValueData.series}
                dateRange={getCartValueDateRange()}
                onPrevious={handleCartValuePreviousWeek}
                onNext={handleCartValueNextWeek}
                canGoPrevious={!isCartValueAtBillingCycleStart}
                canGoNext={!isCartValueAtPresent}
                onReturnToPresent={() => setCartValueDateOffset(0)}
                isAtPresent={isCartValueAtPresent}
                displayMode="percentage"
                subtitle="Sessions with product mentions"
              />
              <div className="flex justify-center mt-4">
                <ViewDetailsButton label="View Details" onClick={() => handleViewDetails('cartValue')} />
              </div>
            </div>

            {/* Sales Performance Chart with Button */}
            <div className="flex flex-col text-center">
              <SalesPerformanceKPI
                conversionRate={salesPerfData.conversionRate}
                deltaPct={salesPerfData.deltaPct}
                orders={salesPerfData.orders}
                ordersDelta={salesPerfData.ordersDelta}
                avgOrderValue={salesPerfData.avgOrderValue}
                avgOrderValueDelta={salesPerfData.avgOrderValueDelta}
                series={salesPerfData.series}
                title="Sales Performance"
                dateRange={getSalesPerfDateRange()}
                onPrevious={handleSalesPerfPreviousWeek}
                onNext={handleSalesPerfNextWeek}
                canGoPrevious={!isSalesPerfAtBillingCycleStart}
                canGoNext={!isSalesPerfAtPresent}
                onReturnToPresent={() => setSalesPerfDateOffset(0)}
                isAtPresent={isSalesPerfAtPresent}
              />
              <div className="flex justify-center mt-4">
                <ViewDetailsButton label="View Details" onClick={() => handleViewDetails('salesPerformance')} />
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t dark:border-border-color border-light-border-color grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-sm dark:text-text-secondary text-light-text-secondary">Cumulative (cycle)</p>
              <p className="text-2xl font-bold dark:text-white text-gray-900">
                €{costMetrics.cumulative.toFixed(2)}
                {costMetrics.trendPct !== null && (
                  <span className={`ml-2 text-sm font-medium ${
                    costMetrics.trendPct > 0 
                      ? 'text-green-500' 
                      : costMetrics.trendPct < 0 
                      ? 'text-red-500' 
                      : 'dark:text-text-secondary text-light-text-secondary'
                  }`}>
                    ({costMetrics.trendPct > 0 ? '↑' : costMetrics.trendPct < 0 ? '↓' : '→'} {Math.abs(costMetrics.trendPct).toFixed(0)}%)
                  </span>
                )}
              </p>
            </div>
            <div>
              <p className="text-sm dark:text-text-secondary text-light-text-secondary">Avg/day</p>
              <p className="text-2xl font-bold dark:text-white text-gray-900">€{costMetrics.avgPerDay.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-sm dark:text-text-secondary text-light-text-secondary">Projected (cycle)</p>
              <p className="text-2xl font-bold dark:text-white text-gray-900">
                €{costMetrics.projected.toFixed(2)}
              </p>
            </div>
          </div>
        </Card>

        {/* Side Cards */}
        <div className="space-y-6">
          <Card title="Subscription Details">
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium dark:text-text-secondary text-light-text-secondary">Current Plan</p>
                <p className="text-xl font-bold text-primary capitalize">{usage.subscription.planId.replace('-', ' ')}</p>
              </div>
              <div>
                <p className="text-sm font-medium dark:text-text-secondary text-light-text-secondary">Status</p>
                <p className={`text-lg font-semibold ${usage.subscription.status === 'active' ? 'text-green-400' : 'text-yellow-400'} capitalize`}>{usage.subscription.status}</p>
              </div>
              <div>
                <p className="text-sm font-medium dark:text-text-secondary text-light-text-secondary">Billing Period</p>
                <div className="flex items-center text-sm dark:text-text-primary text-light-text-primary gap-2">
                  <span>{new Date(usage.subscription.billing.currentPeriodStart).toLocaleDateString()}</span>
                  <ArrowRight className="w-4 h-4 dark:text-text-secondary text-light-text-secondary"/>
                  <span>{new Date(usage.subscription.billing.currentPeriodEnd).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="dark:bg-background bg-gray-100 p-4 rounded-lg text-center">
                <p className="text-sm dark:text-text-secondary text-light-text-secondary">Next billing in</p>
                <p className="text-3xl font-bold dark:text-white text-gray-900">{usage.daysUntilReset}</p>
                <p className="text-sm dark:text-text-secondary text-light-text-secondary">days</p>
              </div>
            </div>
          </Card>

          <Card title="AI Performance Metrics">
            <ul className="space-y-7 text-sm">
              <li className="flex justify-between items-center">
                <span className="flex items-center gap-2 dark:text-text-secondary text-light-text-secondary">
                  <span className="text-lg">💬</span> Conversation Completion Rate
                </span>
                <span className="font-semibold text-green-500">{dashboardData?.data?.aiPerformance?.conversationCompletionRate?.toFixed(0) || 0}%</span>
              </li>
              <li className="flex justify-between items-center">
                <span className="flex items-center gap-2 dark:text-text-secondary text-light-text-secondary">
                  <span className="text-lg">⚡</span> Avg Response Time
                </span>
                <span className="font-semibold text-blue-500">
                  {dashboardData?.data?.aiPerformance?.avgResponseTime 
                    ? `${(dashboardData.data.aiPerformance.avgResponseTime / 1000).toFixed(2)}s`
                    : '0s'
                  }
                </span>
              </li>
              <li className="flex justify-between items-center">
                <span className="flex items-center gap-2 dark:text-text-secondary text-light-text-secondary">
                  <span className="text-lg">😊</span> Customer Satisfaction
                </span>
                <span className="font-semibold text-purple-500">{dashboardData?.data?.aiPerformance?.customerSatisfaction?.toFixed(1) || 0}/5</span>
              </li>
              <li className="flex justify-between items-center">
                <span className="flex items-center gap-2 dark:text-text-secondary text-light-text-secondary">
                  <span className="text-lg">✅</span> Question Resolution Rate
                </span>
                <span className="font-semibold text-yellow-500">{(100 - (dashboardData?.data?.aiPerformance?.errorRate || 0))?.toFixed(0)}%</span>
              </li>
            </ul>
          </Card>
        </div>
      </div>

      {/* Modal */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        title={modalContent?.title}
      >
        {modalContent?.type === 'cumulative' && dashboardData && (
          <div className="space-y-6">
            {/* Usage Split */}
            <div>
              <h3 className="text-lg font-semibold dark:text-text-primary text-light-text-primary mb-4">Usage Distribution</h3>
              <div className="dark:bg-surface bg-light-surface p-6 rounded-lg border dark:border-border-color border-light-border-color">
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium dark:text-text-primary text-light-text-primary">Merchant</span>
                      <span className="text-lg font-bold text-primary">{dashboardData?.data?.usageOverview?.usageDistribution?.merchantPercentage?.toFixed(0) || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                      <div className="bg-gradient-to-r from-primary to-primary-hover h-full rounded-full" style={{ width: `${dashboardData?.data?.usageOverview?.usageDistribution?.merchantPercentage || 0}%` }}></div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium dark:text-text-primary text-light-text-primary">SDK</span>
                      <span className="text-lg font-bold text-green-500">{dashboardData?.data?.usageOverview?.usageDistribution?.sdkPercentage?.toFixed(0) || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                      <div className="bg-gradient-to-r from-green-500 to-green-600 h-full rounded-full" style={{ width: `${dashboardData?.data?.usageOverview?.usageDistribution?.sdkPercentage || 0}%` }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Conversations Breakdown */}
            <div>
              <h3 className="text-lg font-semibold dark:text-text-primary text-light-text-primary mb-4">Conversations</h3>
              <div className="dark:bg-surface bg-light-surface p-6 rounded-lg border dark:border-border-color border-light-border-color">
                <div className="text-center mb-4">
                  <p className="text-sm dark:text-text-secondary text-light-text-secondary mb-1">Total Conversations</p>
                  <p className="text-5xl font-bold dark:text-white text-gray-900">{dashboardData?.data?.usageOverview?.conversationSummary?.total || 0}</p>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <span className={`font-medium flex items-center ${conversationsDelta >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      <span className="text-lg">{conversationsDelta >= 0 ? '↑' : '↓'}</span> {Math.abs(conversationsDelta).toFixed(1)}%
                    </span>
                    <span className="text-sm dark:text-text-secondary text-light-text-secondary">vs last month</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div className="p-4 dark:bg-background bg-gray-50 rounded-lg border dark:border-border-color border-gray-200">
                    <p className="text-xs dark:text-text-secondary text-light-text-secondary mb-1">From SDK</p>
                    <p className="text-3xl font-bold text-green-500">{dashboardData?.data?.usageOverview?.conversationSummary?.sdk || 0}</p>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs dark:text-text-secondary text-light-text-secondary">{(((dashboardData?.data?.usageOverview?.conversationSummary?.sdk || 0) / (dashboardData?.data?.usageOverview?.conversationSummary?.total || 1)) * 100).toFixed(1)}%</p>
                      <span className={`text-xs font-medium flex items-center ${sdkConversationsDelta >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {sdkConversationsDelta >= 0 ? '↑' : '↓'} {Math.abs(sdkConversationsDelta).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  <div className="p-4 dark:bg-background bg-gray-50 rounded-lg border dark:border-border-color border-gray-200">
                    <p className="text-xs dark:text-text-secondary text-light-text-secondary mb-1">From Merchant</p>
                    <p className="text-3xl font-bold text-primary">{dashboardData?.data?.usageOverview?.conversationSummary?.merchant || 0}</p>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs dark:text-text-secondary text-light-text-secondary">{(((dashboardData?.data?.usageOverview?.conversationSummary?.merchant || 0) / (dashboardData?.data?.usageOverview?.conversationSummary?.total || 1)) * 100).toFixed(1)}%</p>
                      <span className={`text-xs font-medium flex items-center ${merchantConversationsDelta >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {merchantConversationsDelta >= 0 ? '↑' : '↓'} {Math.abs(merchantConversationsDelta).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Active Users */}
            <div>
              <h3 className="text-lg font-semibold dark:text-text-primary text-light-text-primary mb-4">Active Users</h3>
              <div className="dark:bg-surface bg-light-surface p-6 rounded-lg border dark:border-border-color border-light-border-color">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-primary to-primary-hover mb-4">
                    <Users className="w-12 h-12 text-white" />
                  </div>
                  <p className="text-sm dark:text-text-secondary text-light-text-secondary mb-1">SDK Active Users</p>
                  <p className="text-5xl font-bold dark:text-white text-gray-900">{dashboardData?.data?.usageOverview?.activeUsersByChannel?.sdk || 0}</p>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <span className={`font-medium flex items-center text-sm ${sdkActiveUsersDelta >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      <span className="text-base">{sdkActiveUsersDelta >= 0 ? '↑' : '↓'}</span> {Math.abs(sdkActiveUsersDelta).toFixed(1)}%
                    </span>
                    <span className="text-xs dark:text-text-secondary text-light-text-secondary">vs last month</span>
                  </div>
                  <p className="text-sm dark:text-text-secondary text-light-text-secondary mt-2">Users actively using the SDK</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {modalContent?.type === 'cartValue' && dashboardData && (
          <div className="space-y-6">
            {/* Product Interest Sessions - Primary Metric */}
            <div>
              <h3 className="text-lg font-semibold dark:text-text-primary text-light-text-primary mb-4">Product Interest Sessions</h3>
              <div className="dark:bg-surface bg-light-surface p-6 rounded-lg border dark:border-border-color border-light-border-color">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm dark:text-text-secondary text-light-text-secondary mb-1">Sessions with Product Mentions</p>
                    <p className="text-4xl font-bold text-primary">{(dashboardData?.data?.productInterest?.sessionsWithProductMentions || 0).toFixed(1)}%</p>
                  </div>
                  <span className={`text-xs rounded-full px-3 py-1 font-medium ${cartValueData.deltaPct >= 0 ? 'bg-green-500/15 text-green-600 dark:text-green-300' : 'bg-red-500/15 text-red-600 dark:text-red-300'}`}>
                    {cartValueData.deltaPct >= 0 ? '↑' : '↓'} {Math.abs(cartValueData.deltaPct).toFixed(1)}%
                  </span>
                </div>
                <p className="text-xs dark:text-text-secondary text-light-text-secondary mb-6">% of sessions where users mentioned a product or category</p>
                
                {/* Intent Type Breakdown */}
                <div className="mt-6 pt-6 border-t dark:border-border-color border-light-border-color">
                  <h4 className="text-sm font-semibold dark:text-text-primary text-light-text-primary mb-3">Session Intent Breakdown</h4>
                  <div className="space-y-3">
                    {(dashboardData?.data?.productInterest?.sessionIntentBreakdown?.intentDetails || []).map((intent, index) => {
                      const colors = [
                        { bg: 'bg-primary', label: 'text-primary' },
                        { bg: 'bg-blue-500', label: 'text-blue-500' },
                        { bg: 'bg-yellow-500', label: 'text-yellow-500' },
                        { bg: 'bg-gray-400', label: 'text-gray-600 dark:text-text-secondary' }
                      ];
                      const color = colors[index % colors.length];
                      
                      return (
                        <div key={intent.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <span className="text-xs sm:text-sm dark:text-text-secondary text-light-text-secondary">{intent.label}</span>
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="w-24 sm:w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                              <div className={`${color.bg} h-full rounded-full`} style={{ width: `${intent.percentage}%` }}></div>
                            </div>
                            <span className={`text-xs sm:text-sm font-semibold dark:text-text-primary text-light-text-primary w-10 text-right`}>{intent.percentage.toFixed(1)}%</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs dark:text-text-secondary text-light-text-secondary mt-3">Benchmarking product-interest vs other intents</p>
                </div>
              </div>
            </div>

            {/* Mini KPIs */}
            <div>
              <h3 className="text-lg font-semibold dark:text-text-primary text-light-text-primary mb-4">Chat Engagement Metrics</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color">
                  <p className="text-xs uppercase tracking-wider dark:text-text-secondary text-light-text-secondary mb-2">Total Product Mentions</p>
                  <p className="text-3xl font-bold dark:text-white text-gray-900">{(dashboardData?.data?.productInterest?.totalProductMentions || 0).toLocaleString()}</p>
                  <p className="text-xs dark:text-text-secondary text-light-text-secondary mt-1">30 days</p>
                </div>
                
                <div className="p-4 dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color">
                  <p className="text-xs uppercase tracking-wider dark:text-text-secondary text-light-text-secondary mb-2">Unique Products Discussed</p>
                  <p className="text-3xl font-bold text-primary">{dashboardData?.data?.productInterest?.uniqueProducts || 0}</p>
                  <p className="text-xs dark:text-text-secondary text-light-text-secondary mt-1">distinct SKUs</p>
                </div>
              </div>
            </div>

            {/* Price Intent Distribution */}
            <div>
              <h3 className="text-lg font-semibold dark:text-text-primary text-light-text-primary mb-4">Price Intent Distribution</h3>
              <div className="dark:bg-surface bg-light-surface p-6 rounded-lg border dark:border-border-color border-light-border-color">
                <div className="space-y-4">
                  {Object.entries(dashboardData?.data?.productInterest?.priceDistribution || {}).map(([range, count], index) => {
                    const total = Object.values(dashboardData?.data?.productInterest?.priceDistribution || {}).reduce((sum: number, val) => sum + (val as number), 0);
                    const percentage = (total as number) > 0 ? ((count as number) / (total as number) * 100).toFixed(1) : 0;
                    const item = { range, percentage };
                    const colors = [
                      { from: 'from-primary', to: 'to-primary-hover', text: 'text-primary' },
                      { from: 'from-green-500', to: 'to-green-600', text: 'text-green-500' },
                      { from: 'from-yellow-500', to: 'to-yellow-600', text: 'text-yellow-500' },
                      { from: 'from-purple-500', to: 'to-purple-600', text: 'text-purple-500' }
                    ];
                    const color = colors[index] || colors[0];
                    
                    return (
                      <div key={item.range}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium dark:text-text-primary text-light-text-primary">{item.range}</span>
                          <span className={`text-lg font-bold ${color.text}`}>{item.percentage}%</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                          <div className={`bg-gradient-to-r ${color.from} ${color.to} h-full rounded-full`} style={{ width: `${item.percentage}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs dark:text-text-secondary text-light-text-secondary mt-4">Distribution of mentioned/suggested product prices</p>
              </div>
            </div>

            {/* Top Products */}
            <div>
              <h3 className="text-lg font-semibold dark:text-text-primary text-light-text-primary mb-4">Top Products</h3>
              <div className="space-y-3">
                {(dashboardData?.data?.productInterest?.topProducts || []).slice(0, 5).map((product, index) => {
                  const totalMentions = dashboardData?.data?.productInterest?.totalProductMentions || 1;
                  const percentage = ((product.mentions / totalMentions) * 100).toFixed(1);
                  
                  const colors = [
                    { from: 'from-primary', to: 'to-primary-hover', text: 'text-primary' },
                    { from: 'from-green-500', to: 'to-green-600', text: 'text-green-500' },
                    { from: 'from-yellow-500', to: 'to-yellow-600', text: 'text-yellow-500' },
                    { from: 'from-gray-400', to: 'to-gray-500', text: 'text-gray-600 dark:text-text-secondary' }
                  ];
                  const color = colors[index] || colors[colors.length - 1];
                  
                  return (
                    <div key={product.sku} className="p-4 dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium dark:text-text-primary text-light-text-primary truncate mr-2">{product.name}</span>
                        <span className={`text-lg font-bold ${color.text}`}>{product.mentions}</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                        <div className={`bg-gradient-to-r ${color.from} ${color.to} h-full rounded-full`} style={{ width: `${percentage}%` }}></div>
                      </div>
                      <p className="text-xs dark:text-text-secondary text-light-text-secondary mt-1">{percentage}% of mentions</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Insight Box */}
            <div className="p-4 rounded-lg dark:bg-blue-900/20 bg-blue-50 border border-blue-500/20">
              <p className="text-sm dark:text-blue-200 text-blue-800">
                💡 <strong>Insight:</strong> These metrics are derived entirely from chat interactions and product suggestions made by Mentoros.
              </p>
            </div>
          </div>
        )}
        
        {modalContent?.type === 'salesPerformance' && dashboardData && (
          <div className="space-y-6">
            {/* Shopping Intent Performance */}
            <div>
              <h3 className="text-lg font-semibold dark:text-text-primary text-light-text-primary mb-4">Shopping Intent Performance</h3>
              
              {/* Intent Conversion Rate - Primary Metric */}
              <div className="dark:bg-surface bg-light-surface p-6 rounded-lg border dark:border-border-color border-light-border-color mb-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm dark:text-text-secondary text-light-text-secondary mb-1">Intent Conversion Rate</p>
                    <p className="text-4xl font-bold text-green-500">{(dashboardData?.data?.salesPerformance?.intentConversionRate || 0).toFixed(1)}%</p>
                  </div>
                  <span className={`text-xs rounded-full px-3 py-1 font-medium ${salesPerfData.deltaPct >= 0 ? 'bg-green-500/15 text-green-600 dark:text-green-300' : 'bg-red-500/15 text-red-600 dark:text-red-300'}`}>
                    {salesPerfData.deltaPct >= 0 ? '↑' : '↓'} {Math.abs(salesPerfData.deltaPct).toFixed(1)}%
                  </span>
                </div>
                <p className="text-xs dark:text-text-secondary text-light-text-secondary">Sessions with 'add/buy' expressions</p>
              </div>

              {/* Intent Performance Grid - 4 KPIs */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-4 dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color">
                  <p className="text-xs uppercase tracking-wider dark:text-text-secondary text-light-text-secondary mb-2">Cart Intents Generated</p>
                  <p className="text-3xl font-bold text-primary">{dashboardData?.data?.salesPerformance?.cartIntentsGenerated || 0}</p>
                  <p className="text-xs dark:text-text-secondary text-light-text-secondary mt-1">30 days</p>
                  <div className="mt-2">
                    <span className={`text-xs font-medium ${salesPerfData.ordersDelta >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {salesPerfData.ordersDelta >= 0 ? '↑' : '↓'} {Math.abs(salesPerfData.ordersDelta).toFixed(1)}%
                    </span>
                  </div>
                </div>
                
                <div className="p-4 dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color">
                  <p className="text-xs uppercase tracking-wider dark:text-text-secondary text-light-text-secondary mb-2">Avg Cart Value</p>
                  <p className="text-3xl font-bold text-green-500">€{(dashboardData?.data?.salesPerformance?.avgCartValue || 0).toFixed(2)}</p>
                  <p className="text-xs dark:text-text-secondary text-light-text-secondary mt-1">AI-estimated</p>
                  <div className="mt-2">
                    <span className={`text-xs font-medium ${salesPerfData.avgOrderValueDelta >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {salesPerfData.avgOrderValueDelta >= 0 ? '↑' : '↓'} {Math.abs(salesPerfData.avgOrderValueDelta).toFixed(1)}%
                    </span>
                  </div>
                </div>
                
                <div className="p-4 dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color">
                  <p className="text-xs uppercase tracking-wider dark:text-text-secondary text-light-text-secondary mb-2">Avg Products per Intent</p>
                  <p className="text-3xl font-bold text-blue-500">{(dashboardData?.data?.salesPerformance?.avgProductsPerIntent || 0).toFixed(1)}</p>
                  <p className="text-xs dark:text-text-secondary text-light-text-secondary mt-1">items per intent session</p>
                </div>
                
                <div className="p-4 dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color">
                  <p className="text-xs uppercase tracking-wider dark:text-text-secondary text-light-text-secondary mb-2">Avg Messages to Intent</p>
                  <p className="text-3xl font-bold text-purple-500">{(dashboardData?.data?.salesPerformance?.avgMessagesToFirstIntent || 0).toFixed(1)}</p>
                  <p className="text-xs dark:text-text-secondary text-light-text-secondary mt-1">messages to first intent</p>
                </div>
              </div>

              {/* Intent Funnel */}
              <div className="dark:bg-surface bg-light-surface p-6 rounded-lg border dark:border-border-color border-light-border-color">
                <h4 className="text-sm font-semibold dark:text-text-primary text-light-text-primary mb-4">Intent Funnel</h4>
                <div className="space-y-4">
                  {(dashboardData?.data?.salesPerformance?.intentFunnel?.stages || []).map((stage, index) => {
                    const colors = [
                      { bg: 'bg-primary', ring: 'bg-primary/20', text: 'text-primary', gradient: 'from-primary to-primary-hover' },
                      { bg: 'bg-green-500', ring: 'bg-green-500/20', text: 'text-green-500', gradient: 'from-green-500 to-green-600' },
                      { bg: 'bg-yellow-500', ring: 'bg-yellow-500/20', text: 'text-yellow-500', gradient: 'from-yellow-500 to-yellow-600' }
                    ];
                    const color = colors[index] || colors[0];
                    const maxValue = dashboardData?.data?.salesPerformance?.intentFunnel?.stages?.[0]?.value || 1;
                    const widthPercent = (stage.value / maxValue) * 100;
                    
                    return (
                      <div key={stage.key}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className={`w-6 h-6 rounded-full ${color.ring} flex items-center justify-center text-xs font-bold ${color.text}`}>{index + 1}</div>
                            <span className="text-sm font-medium dark:text-text-primary text-light-text-primary">{stage.label}</span>
                          </div>
                          <span className={`text-lg font-bold ${color.text}`}>
                            {stage.key === 'potential_cart_value' ? `€${stage.value.toFixed(2)}` : stage.value}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                          <div className={`bg-gradient-to-r ${color.gradient} h-full rounded-full`} style={{ width: `${widthPercent}%` }}></div>
                        </div>
                        <p className="text-xs dark:text-text-secondary text-light-text-secondary mt-1">
                          {stage.conversionRate ? `${stage.conversionRate.toFixed(1)}% conversion` : ''}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Insight */}
            <div className="p-4 rounded-lg dark:bg-blue-900/20 bg-blue-50 border border-blue-500/20">
              <p className="text-sm dark:text-blue-200 text-blue-800">
                💡 <strong>Intent Conversion Rate</strong> measures the percentage of product-interest sessions that resulted in add/buy intent expressions. This shows how effectively Mentoros creates buying intent - <strong>entirely from chat signals</strong>, no checkout data needed!
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DashboardPage;
